import dayjs, { type Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

/**
 * The console's date library, configured once.
 *
 * Import this rather than `dayjs` directly: the plugins are registered here,
 * and a module that imports dayjs itself gets the bare build, where
 * `.fromNow()` and `.utc()` are silently undefined.
 *
 * There used to be two date libraries. Seven components imported `moment`
 * while the lists used `dayjs` — and `moment` was never in package.json at
 * all, so it only resolved because some other dependency happened to hoist it
 * into node_modules. Removing an unrelated package took it away and broke the
 * build, which is the first anyone knew about it.
 */
dayjs.extend(utc);
dayjs.extend(relativeTime);

export default dayjs;

/*
 * How a date reads on screen, in one place.
 *
 * Before these existed the console showed seven formats, two of them side by side in the Jobs
 * table ("28/09/2026" posted, "28 Sep 2026" created), and the browser-locale ones changed with
 * whoever was looking: Users read "7/11/2026" in one office and "11/7/2026" in another, which is
 * a different day. Every table and list goes through these:
 *
 *   formatDate      28 Sep 2026
 *   formatDateTime  28 Sep 2026, 9:37 pm
 *   formatDayMonth  28 Sep            (a compact column where the year is obvious, e.g. "due")
 *
 * Relative times ("4h ago") stay relative where a page already shows them; they answer a
 * different question.
 */
export const DATE_FORMAT = 'D MMM YYYY';
export const DATE_TIME_FORMAT = 'D MMM YYYY, h:mm a';
export const DAY_MONTH_FORMAT = 'D MMM';

/** What an absent date renders as, so a table never mixes '', '-' and 'N/A'. */
export const NO_DATE = '-';

export type DateInput = string | number | Date | Dayjs | null | undefined;

/**
 * Parses what the backend sends.
 *
 * The Java service stores UTC and some fields serialise as a LocalDateTime with no offset
 * ("2026-10-06T16:07:58"). Read as local time, that is 5h30 early in India, which moved a
 * late-evening signup onto the previous day. Job Seekers, Employers and Notifications each
 * carried a private `parseUtcDate` for exactly this. A bare date ("2026-08-13") has no time and
 * stays a calendar date, so it is not shifted.
 */
export const parseDate = (value: DateInput): Dayjs | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const naiveDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(trimmed);
    const d = naiveDateTime ? dayjs.utc(trimmed).local() : dayjs(trimmed);
    return d.isValid() ? d : null;
  }
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const formatWith = (value: DateInput, pattern: string, empty: string): string => {
  const d = parseDate(value);
  return d ? d.format(pattern) : empty;
};

/** "28 Sep 2026" */
export const formatDate = (value: DateInput, empty: string = NO_DATE): string =>
  formatWith(value, DATE_FORMAT, empty);

/** "28 Sep 2026, 9:37 pm" */
export const formatDateTime = (value: DateInput, empty: string = NO_DATE): string =>
  formatWith(value, DATE_TIME_FORMAT, empty);

/** "28 Sep" */
export const formatDayMonth = (value: DateInput, empty: string = NO_DATE): string =>
  formatWith(value, DAY_MONTH_FORMAT, empty);

/**
 * "Just now", "4h ago", "3d ago", then the plain date from a week out.
 *
 * Moved out of Job Seekers and Employers, which each had a copy; a relative time older than a
 * week stops being easier to read than the date.
 */
export const formatRelative = (value: DateInput, empty = 'Never', now: number = Date.now()): string => {
  const d = parseDate(value);
  if (!d) return empty;
  const diffMs = now - d.valueOf();
  const hours = Math.floor(diffMs / 3_600_000);
  const days = Math.floor(diffMs / 86_400_000);
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.format(DATE_FORMAT);
};

/**
 * The calendar date `days` ago as yyyy-MM-dd, in UTC: the shape the backend's date filters take,
 * and the zone its servers bound a day in. `isoDaysAgo(0)` is today. Shared by the dashboard
 * periods and the City tab, so "last 30 days" starts on the same day in both.
 */
export const isoDaysAgo = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};
